"use client";

import { rmcClient } from "@/lib/rmcClient";
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useReactToPrint } from "react-to-print";

const RawMaterialsListPrint = forwardRef(function RawMaterialsListPrint(_, ref) {
  const contentRef = useRef(null);
  const [company, setCompany] = useState(null);
  const [rows, setRows] = useState(null);

  useEffect(() => {
    (async () => {
      const c = (await rmcClient.getCompany()).data;
      setCompany(c);
    })();
  }, []);

  const printFn = useReactToPrint({
    contentRef,
    documentTitle: "raw-materials",
  });

  useImperativeHandle(ref, () => ({
    openPrint: async (params = {}) => {
      const data = (await rmcClient.listMaterials(params)).data;
      flushSync(() => {
        setRows(Array.isArray(data) ? data : []);
      });
      await new Promise((resolve) => setTimeout(resolve, 0));
      printFn();
    },
  }));

  const companyName = company?.name || company?.company_name || "";
  const materials = rows ?? [];

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed left-0 top-0 -z-10 h-0 w-0 overflow-hidden opacity-0 print:static print:z-50 print:h-auto print:w-auto print:overflow-visible print:opacity-100"
    >
      <div ref={contentRef} className="bg-white p-6 text-black print:block">
        <div className="mx-auto max-w-[210mm]">
          {companyName ? (
            <p className="mb-1 text-center text-sm font-bold">{companyName}</p>
          ) : null}
          <h1 className="mb-1 text-center text-base font-bold">Raw materials</h1>
          <p className="mb-4 text-center text-xs text-slate-700">All materials</p>
          <table className="w-full border-collapse border border-slate-800 text-xs">
            <thead>
              <tr className="bg-slate-100">
                <th className="border border-slate-800 px-2 py-1.5 text-left font-semibold">
                  Raw material ID
                </th>
                <th className="border border-slate-800 px-2 py-1.5 text-left font-semibold">
                  Raw material name
                </th>
                <th className="border border-slate-800 px-2 py-1.5 text-left font-semibold">
                  Wastage %
                </th>
              </tr>
            </thead>
            <tbody>
              {materials.length === 0 ? (
                <tr>
                  <td colSpan={3} className="border border-slate-800 px-2 py-2 text-slate-600">
                    No records
                  </td>
                </tr>
              ) : (
                materials.map((row) => (
                  <tr key={row.id}>
                    <td className="border border-slate-800 px-2 py-1.5 tabular-nums">{row.id}</td>
                    <td className="border border-slate-800 px-2 py-1.5">{row.name ?? "—"}</td>
                    <td className="border border-slate-800 px-2 py-1.5">
                      {row.wastage == null || row.wastage === ""
                        ? "—"
                        : `${Number(row.wastage).toFixed(2)}%`}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
});

RawMaterialsListPrint.displayName = "RawMaterialsListPrint";

export default RawMaterialsListPrint;
